import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Customer, CustomerMetrics } from "@/core";
import { useCustomerMetrics } from "@/hooks";
import { formatPrice } from "@/utils/pricing-formatter";
import { Loader2, RefreshCw } from "lucide-react";

interface MetricsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer;
}

const formatCount = (count?: number | null) => {
  if (count === null || count === undefined) return "-";
  return new Intl.NumberFormat("en-US").format(count);
};

// Normalize Firestore Timestamp-like objects nested in documents
const normalizeDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  // Firestore Timestamp has seconds/nanoseconds shape
  const maybeTs = value as { seconds?: number; nanoseconds?: number };
  if (
    typeof maybeTs?.seconds === "number" &&
    typeof maybeTs?.nanoseconds === "number"
  ) {
    return new Date(
      maybeTs.seconds * 1000 + Math.floor(maybeTs.nanoseconds / 1_000_000),
    );
  }
  // Fallback to Date constructor for strings/numbers
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export const MetricsDialog = ({
  open,
  onOpenChange,
  customer,
}: MetricsDialogProps) => {
  const metricsQuery = useCustomerMetrics(customer.id);
  const refetch = () => metricsQuery.refetch();

  const metrics = metricsQuery.data as
    | Partial<
        CustomerMetrics & {
          potentialAirlineMilesMetrics?: CustomerMetrics["airlineMilesMetrics"];
          potentialHotelPointsMetrics?: CustomerMetrics["airlineMilesMetrics"];
          potentialCreditCardPointsMetrics?: CustomerMetrics["airlineMilesMetrics"];
          realisedAirlineMilesMetrics?: CustomerMetrics["airlineMilesMetrics"];
          realisedHotelPointsMetrics?: CustomerMetrics["airlineMilesMetrics"];
          realisedCreditCardPointsMetrics?: CustomerMetrics["airlineMilesMetrics"];
        }
      >
    | null
    | undefined;
  const emptyRfmMetrics: CustomerMetrics["airlineMilesMetrics"] = {
    buyOrderCount: 0,
    buyOrderTotalValue: 0,
    lastBuyDate: null,
    sellOrderCount: 0,
    sellOrderTotalValue: 0,
    lastSellDate: null,
  };

  type Rfm = CustomerMetrics["airlineMilesMetrics"];
  interface SeparatedMetricsData {
    potential: {
      airlineMiles: Rfm;
      hotelPoints: Rfm;
      creditCardPoints: Rfm;
    };
    realised: {
      airlineMiles: Rfm;
      hotelPoints: Rfm;
      creditCardPoints: Rfm;
    };
  }

  // Map backend keys to separated potential/realised metrics
  const mapToUiMetrics = (
    raw: NonNullable<typeof metrics>,
  ): SeparatedMetricsData => {
    const normalize = (
      m: CustomerMetrics["airlineMilesMetrics"] | undefined,
    ) => ({
      buyOrderCount: m?.buyOrderCount ?? 0,
      buyOrderTotalValue: m?.buyOrderTotalValue ?? 0,
      lastBuyDate: normalizeDate(m?.lastBuyDate),
      sellOrderCount: m?.sellOrderCount ?? 0,
      sellOrderTotalValue: m?.sellOrderTotalValue ?? 0,
      lastSellDate: normalizeDate(m?.lastSellDate),
    });

    return {
      potential: {
        airlineMiles: normalize(
          raw?.potentialAirlineMilesMetrics ?? emptyRfmMetrics,
        ),
        hotelPoints: normalize(
          raw?.potentialHotelPointsMetrics ?? emptyRfmMetrics,
        ),
        creditCardPoints: normalize(
          raw?.potentialCreditCardPointsMetrics ?? emptyRfmMetrics,
        ),
      },
      realised: {
        airlineMiles: normalize(
          raw?.realisedAirlineMilesMetrics ?? emptyRfmMetrics,
        ),
        hotelPoints: normalize(
          raw?.realisedHotelPointsMetrics ?? emptyRfmMetrics,
        ),
        creditCardPoints: normalize(
          raw?.realisedCreditCardPointsMetrics ?? emptyRfmMetrics,
        ),
      },
    };
  };

  const safeMetricsData: SeparatedMetricsData | null = metrics
    ? mapToUiMetrics(metrics)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-3xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle>Customer Metrics - {customer.firstName}</SheetTitle>
          <SheetDescription>
            Aggregated metrics for Air Miles, Hotel, and Credit activity across
            buy and sell orders.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {metricsQuery.isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Loading Metrics</h3>
              <p className="text-muted-foreground">
                Fetching customer metrics...
              </p>
            </div>
          )}

          {!metricsQuery.isLoading && !safeMetricsData && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <h3 className="text-lg font-semibold mb-2">No Metrics Yet</h3>
              <p className="text-muted-foreground">
                This customer has no recorded metrics yet.
              </p>
            </div>
          )}

          {!metricsQuery.isLoading && safeMetricsData && (
            <div className="space-y-6">
              <div className="flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refetch}
                  disabled={metricsQuery.isFetching}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${metricsQuery.isFetching ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
              </div>

              <div className="space-y-6">
                <div className="border rounded-lg">
                  <div className="px-4 py-3 font-medium">Potential Summary</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Buy Orders</TableHead>
                        <TableHead>Buy Total</TableHead>
                        <TableHead>Last Buy</TableHead>
                        <TableHead>Sell Orders</TableHead>
                        <TableHead>Sell Total</TableHead>
                        <TableHead>Last Sell</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { key: "airlineMiles" as const, label: "Air Miles" },
                        { key: "hotelPoints" as const, label: "Hotel" },
                        { key: "creditCardPoints" as const, label: "Credit" },
                      ].map(({ key, label }) => {
                        const m = safeMetricsData.potential[key];
                        return (
                          <TableRow key={key}>
                            <TableCell>{label}</TableCell>
                            <TableCell>
                              {formatCount(m.buyOrderCount)}
                            </TableCell>
                            <TableCell>
                              {formatPrice({ price: m.buyOrderTotalValue })}
                            </TableCell>
                            <TableCell>
                              {m.lastBuyDate
                                ? new Date(m.lastBuyDate).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {formatCount(m.sellOrderCount)}
                            </TableCell>
                            <TableCell>
                              {formatPrice({ price: m.sellOrderTotalValue })}
                            </TableCell>
                            <TableCell>
                              {m.lastSellDate
                                ? new Date(m.lastSellDate).toLocaleDateString()
                                : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="border rounded-lg">
                  <div className="px-4 py-3 font-medium">Realised Summary</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Buy Orders</TableHead>
                        <TableHead>Buy Total</TableHead>
                        <TableHead>Last Buy</TableHead>
                        <TableHead>Sell Orders</TableHead>
                        <TableHead>Sell Total</TableHead>
                        <TableHead>Last Sell</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { key: "airlineMiles" as const, label: "Air Miles" },
                        { key: "hotelPoints" as const, label: "Hotel" },
                        { key: "creditCardPoints" as const, label: "Credit" },
                      ].map(({ key, label }) => {
                        const m = safeMetricsData.realised[key];
                        return (
                          <TableRow key={key}>
                            <TableCell>{label}</TableCell>
                            <TableCell>
                              {formatCount(m.buyOrderCount)}
                            </TableCell>
                            <TableCell>
                              {formatPrice({ price: m.buyOrderTotalValue })}
                            </TableCell>
                            <TableCell>
                              {m.lastBuyDate
                                ? new Date(m.lastBuyDate).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {formatCount(m.sellOrderCount)}
                            </TableCell>
                            <TableCell>
                              {formatPrice({ price: m.sellOrderTotalValue })}
                            </TableCell>
                            <TableCell>
                              {m.lastSellDate
                                ? new Date(m.lastSellDate).toLocaleDateString()
                                : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
